/*
 * Copyright (C) 2018 Frontier Developments PLC.
 */

import {ActionTree} from 'vuex';
import {RootState} from '@/store/types';
import {AxiosResponse} from 'axios';
import api from '@/services/api';
import {
    UserAuthenticationRequestInterface,
    UserSessionInterface,
    UserSessionStateInterface,
} from '@/store/modules/user-session/types';

/**
 * Actions file for user session operations.
 *
 * Actions are functions that can be exported and called upon potentially in an asynchronous manner.
 * To add an action export a named const function. e.g. export const fnName = ({ commit }, {id, name}) => { ... }
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export const actions: ActionTree<UserSessionStateInterface, RootState> = {
    /**
     * Authenticate a user.
     *
     * @param {any} commit
     * @param {UserAuthenticationRequestInterface} params
     *
     * @returns {Promise<any>}
     */
    authenticate({commit}: {commit: any}, params: UserAuthenticationRequestInterface): Promise<UserSessionInterface> {
        return api.axios.post('auth/steam', JSON.stringify(params), {
                params: {
                    noContentTypeHeader: true,
                 },
            })
            .then((response: AxiosResponse<UserSessionInterface>) => {
                    commit('authenticate', response.data);
                    return response.data;
                });
    },

    /**
     * Confirm that the user has read the terms and conditions of service.
     *
     * @param {any} commit
     *
     * @returns {Promise<AxiosResponse>}
     */
    readTerms({commit}: {commit: any}): Promise<AxiosResponse> {
        return api.axios.put('auth/terms');
    },

    /**
     * Logout of the user session.
     *
     * As we are using JWT tokens we just need to remove the reference to the token in the localStorage.
     *
     * @param {Promise<AxiosResponse>} commit
     */
    logout({commit}: {commit: any}): Promise<AxiosResponse> {
        return api.axios.delete('api/user/session')
            .then((response: AxiosResponse<void>) => {
                commit('logout');
                return response;
            }).catch((response: AxiosResponse<void>) => {
                commit('logout');
                return response;
            });
    },

    /**
     * Soft logout of the user session.  This is a logout that doens't touch api.
     *
     * As we are using JWT tokens we just need to remove the reference to the token in the localStorage.
     *
     * @param {Promise<AxiosResponse>} commit
     */
    softLogout({commit}: {commit: any}): void {
        commit('logout');
    },
};
